import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { settingsAPI } from '../../services/api';

const BRANDING_CACHE_KEY = 'cachedBranding';

function getCachedBranding() {
  try {
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function setCachedBranding(branding) {
  try {
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(branding));
  } catch { /* ignore quota errors */ }
}

const cachedBranding = getCachedBranding();

export const fetchTenantConfig = createAsyncThunk('settings/fetchConfig', async (_, { rejectWithValue }) => {
  try {
    const res = await settingsAPI.get();
    return res.data.config;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Failed to fetch settings');
  }
});

export const fetchBranding = createAsyncThunk('settings/fetchBranding', async (_, { rejectWithValue }) => {
  try {
    const res = await settingsAPI.getBranding();
    const branding = res.data.branding;
    // Refresh the cache whenever ANY part of the payload changed. This used to
    // compare four hand-picked fields (accent, company name, logo, language),
    // which meant a new hero image, a changed login layout or new slider slides
    // never invalidated the cache — the login page kept painting the old
    // branding until localStorage was cleared.
    const cached = getCachedBranding();
    if (JSON.stringify(cached) !== JSON.stringify(branding)) {
      setCachedBranding(branding);
    }
    return branding;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Failed to fetch branding');
  }
});

export const fetchRoles = createAsyncThunk('settings/fetchRoles', async (_, { rejectWithValue }) => {
  try {
    const res = await settingsAPI.getRoles();
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Failed to fetch roles');
  }
});

const settingsSlice = createSlice({
  name: 'settings',
  initialState: {
    config: null,
    branding: cachedBranding,
    brandingLoading: !cachedBranding,
    roles: [],
    permissions: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenantConfig.pending, (state) => { state.loading = true; })
      .addCase(fetchTenantConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.config = action.payload;
      })
      .addCase(fetchTenantConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchBranding.pending, (state) => {
        if (!state.branding) state.brandingLoading = true;
      })
      .addCase(fetchBranding.fulfilled, (state, action) => {
        state.brandingLoading = false;
        state.branding = action.payload;
      })
      .addCase(fetchBranding.rejected, (state) => {
        state.brandingLoading = false;
        if (!state.branding) {
          state.branding = { companyName: '', logoUrl: '', accentColor: '#6366f1' };
        }
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.roles = action.payload.roles;
        state.permissions = action.payload.permissions;
      });
  },
});

export default settingsSlice.reducer;
